import classNames from "classnames/bind";
import React from "react";
import { Skeleton } from "../../../Skeleton";
import { stripInvalidPropsForElement } from "../../../utils/component-props";
import { InputDescription } from "../InputDescription";
import styles from "./InputLabel.module.scss";
import type { InputLabelProps } from "./types";

const cx = classNames.bind(styles);

/**
 * Renders the input's label, and long-form description, if provided.
 *
 * Note, the label is provided through children and is rendered in a span within
 * a label element.
 */
export const InputLabel = ({
  children,
  inputId,
  isRequired,
  isDisabled,
  isLoading,
  isBusy,
  description,
  shouldShowDescriptionInTooltip,
  isOptionalTextShown,
  className,
  ...restProps
}: InputLabelProps) => {
  return (
    <Skeleton
      isLoading={isLoading}
      isBusy={isBusy}
    >
      <label
        htmlFor={inputId}
        className={cx("label", className)}
        aria-disabled={isDisabled}
        {...stripInvalidPropsForElement(restProps, "label")}
      >
        <span className={cx("label-text")}>{children}</span>
        {description ? (
          shouldShowDescriptionInTooltip ? (
            <InputDescription
              colorScheme="light"
              isDisabled={isDisabled}
              isLoading={isLoading}
              isBusy={isBusy}
              className={cx("icon-container")}
              iconProps={{
                "aria-roledescription": "More information",
              }}
              aria-label="More information about this input"
            >
              {description}
            </InputDescription>
          ) : (
            <span className={cx("information")}>{description}</span>
          )
        ) : null}
        {!isRequired && isOptionalTextShown && (
          <span className={cx("modifier", { "label--disabled": isDisabled })}>
            Optional
          </span>
        )}
      </label>
    </Skeleton>
  );
};
